// Constantes pour les fonctions du personnel selon le type d'entreprise (PME vs Startup)

// ============================================================================
// FONCTIONS POUR LES DIRIGEANTS
// ============================================================================

// Fonctions communes à tous les types d'entreprises
export const COMMON_EXECUTIVE_FUNCTIONS = [
  { value: 'directeur_general', label: 'Directeur Général (DG)', description: 'Responsable exécutif principal' },
  { value: 'president', label: 'Président', description: 'Président du conseil ou de l\'entreprise' },
  { value: 'gerant', label: 'Gérant', description: 'Gérant (SARL, SNC)' },
  { value: 'administrateur_general', label: 'Administrateur Général', description: 'Pour les SA avec AG' },
  { value: 'directeur_general_adjoint', label: 'Directeur Général Adjoint (DGA)', description: 'Second du DG' },
  { value: 'secretaire_general', label: 'Secrétaire Général', description: 'Coordination administrative' },
] as const;

// Fonctions spécifiques aux PME traditionnelles
export const PME_EXECUTIVE_FUNCTIONS = [
  { value: 'directeur_administratif', label: 'Directeur Administratif', description: 'Gestion administrative' },
  { value: 'directeur_commercial', label: 'Directeur Commercial', description: 'Direction des ventes' },
  { value: 'directeur_financier', label: 'Directeur Administratif et Financier (DAF)', description: 'Finances et comptabilité' },
  { value: 'directeur_technique', label: 'Directeur Technique', description: 'Direction technique/production' },
  { value: 'directeur_ressources_humaines', label: 'Directeur des Ressources Humaines (DRH)', description: 'Gestion du personnel' },
  { value: 'directeur_production', label: 'Directeur de Production', description: 'Supervision de la production' },
  { value: 'directeur_achats', label: 'Directeur des Achats', description: 'Gestion des approvisionnements' },
  { value: 'directeur_logistique', label: 'Directeur Logistique', description: 'Gestion de la chaîne logistique' },
  { value: 'directeur_qualite', label: 'Directeur Qualité', description: 'Assurance qualité' },
  { value: 'directeur_exploitation', label: 'Directeur d\'Exploitation', description: 'Opérations courantes' },
  { value: 'chef_entreprise', label: 'Chef d\'Entreprise', description: 'Propriétaire-dirigeant' },
  { value: 'fondateur', label: 'Fondateur', description: 'Créateur de l\'entreprise' },
] as const;

// Fonctions spécifiques aux Startups
export const STARTUP_EXECUTIVE_FUNCTIONS = [
  { value: 'ceo', label: 'CEO (Chief Executive Officer)', description: 'Directeur exécutif' },
  { value: 'coo', label: 'COO (Chief Operating Officer)', description: 'Directeur des opérations' },
  { value: 'cto', label: 'CTO (Chief Technology Officer)', description: 'Directeur technique' },
  { value: 'cfo', label: 'CFO (Chief Financial Officer)', description: 'Directeur financier' },
  { value: 'cmo', label: 'CMO (Chief Marketing Officer)', description: 'Directeur marketing' },
  { value: 'cpo', label: 'CPO (Chief Product Officer)', description: 'Directeur produit' },
  { value: 'cro', label: 'CRO (Chief Revenue Officer)', description: 'Directeur revenus' },
  { value: 'cso', label: 'CSO (Chief Strategy Officer)', description: 'Directeur stratégie' },
  { value: 'cdo', label: 'CDO (Chief Data Officer)', description: 'Directeur données' },
  { value: 'ciso', label: 'CISO (Chief Information Security Officer)', description: 'Directeur sécurité informatique' },
  { value: 'vp_engineering', label: 'VP Engineering', description: 'Vice-président ingénierie' },
  { value: 'vp_product', label: 'VP Product', description: 'Vice-président produit' },
  { value: 'vp_sales', label: 'VP Sales', description: 'Vice-président ventes' },
  { value: 'vp_growth', label: 'VP Growth', description: 'Vice-président croissance' },
  { value: 'head_of_engineering', label: 'Head of Engineering', description: 'Responsable ingénierie' },
  { value: 'head_of_product', label: 'Head of Product', description: 'Responsable produit' },
  { value: 'head_of_design', label: 'Head of Design', description: 'Responsable design' },
  { value: 'founder', label: 'Founder', description: 'Fondateur' },
  { value: 'cofounder', label: 'Co-Founder', description: 'Co-fondateur' },
] as const;

// ============================================================================
// FONCTIONS POUR LES EMPLOYÉS
// ============================================================================

// Fonctions employés PME traditionnelles
export const PME_EMPLOYEE_FUNCTIONS = [
  // Management intermédiaire
  { value: 'chef_service', label: 'Chef de Service', category: 'Management' },
  { value: 'chef_departement', label: 'Chef de Département', category: 'Management' },
  { value: 'responsable_equipe', label: 'Responsable d\'Équipe', category: 'Management' },
  { value: 'superviseur', label: 'Superviseur', category: 'Management' },
  { value: 'contremaître', label: 'Contremaître', category: 'Management' },
  
  // Administration
  { value: 'assistant_direction', label: 'Assistant(e) de Direction', category: 'Administration' },
  { value: 'secretaire', label: 'Secrétaire', category: 'Administration' },
  { value: 'receptionniste', label: 'Réceptionniste', category: 'Administration' },
  { value: 'agent_administratif', label: 'Agent Administratif', category: 'Administration' },
  { value: 'archiviste', label: 'Archiviste', category: 'Administration' },
  
  // Finance & Comptabilité
  { value: 'comptable', label: 'Comptable', category: 'Finance' },
  { value: 'aide_comptable', label: 'Aide-Comptable', category: 'Finance' },
  { value: 'caissier', label: 'Caissier(ère)', category: 'Finance' },
  { value: 'controleur_gestion', label: 'Contrôleur de Gestion', category: 'Finance' },
  { value: 'tresorier', label: 'Trésorier', category: 'Finance' },
  { value: 'facturier', label: 'Facturier', category: 'Finance' },
  
  // Commercial & Ventes
  { value: 'commercial', label: 'Commercial(e)', category: 'Commercial' },
  { value: 'vendeur', label: 'Vendeur(se)', category: 'Commercial' },
  { value: 'chef_ventes', label: 'Chef des Ventes', category: 'Commercial' },
  { value: 'representant', label: 'Représentant Commercial', category: 'Commercial' },
  { value: 'charge_clientele', label: 'Chargé(e) de Clientèle', category: 'Commercial' },
  { value: 'responsable_commercial', label: 'Responsable Commercial', category: 'Commercial' },
  
  // Production & Technique
  { value: 'technicien', label: 'Technicien', category: 'Technique' },
  { value: 'operateur_machine', label: 'Opérateur Machine', category: 'Technique' },
  { value: 'ouvrier_qualifie', label: 'Ouvrier Qualifié', category: 'Technique' },
  { value: 'ouvrier_specialise', label: 'Ouvrier Spécialisé', category: 'Technique' },
  { value: 'mecanicien', label: 'Mécanicien', category: 'Technique' },
  { value: 'electricien', label: 'Électricien', category: 'Technique' },
  { value: 'soudeur', label: 'Soudeur', category: 'Technique' },
  { value: 'agent_maintenance', label: 'Agent de Maintenance', category: 'Technique' },
  { value: 'responsable_qualite', label: 'Responsable Qualité', category: 'Technique' },
  
  // Logistique & Supply Chain
  { value: 'magasinier', label: 'Magasinier', category: 'Logistique' },
  { value: 'gestionnaire_stock', label: 'Gestionnaire de Stock', category: 'Logistique' },
  { value: 'chauffeur', label: 'Chauffeur', category: 'Logistique' },
  { value: 'livreur', label: 'Livreur', category: 'Logistique' },
  { value: 'manutentionnaire', label: 'Manutentionnaire', category: 'Logistique' },
  { value: 'responsable_logistique', label: 'Responsable Logistique', category: 'Logistique' },
  
  // Ressources Humaines
  { value: 'charge_rh', label: 'Chargé(e) RH', category: 'RH' },
  { value: 'responsable_paie', label: 'Responsable Paie', category: 'RH' },
  { value: 'assistant_rh', label: 'Assistant(e) RH', category: 'RH' },
  { value: 'charge_recrutement', label: 'Chargé(e) de Recrutement', category: 'RH' },
  
  // Services généraux
  { value: 'agent_securite', label: 'Agent de Sécurité', category: 'Services' },
  { value: 'agent_entretien', label: 'Agent d\'Entretien', category: 'Services' },
  { value: 'gardien', label: 'Gardien', category: 'Services' },
  { value: 'cuisinier', label: 'Cuisinier', category: 'Services' },
  { value: 'chauffeur_direction', label: 'Chauffeur de Direction', category: 'Services' },
] as const;

// Fonctions employés Startups
export const STARTUP_EMPLOYEE_FUNCTIONS = [
  // Engineering & Tech
  { value: 'software_engineer', label: 'Software Engineer', category: 'Engineering' },
  { value: 'senior_software_engineer', label: 'Senior Software Engineer', category: 'Engineering' },
  { value: 'staff_engineer', label: 'Staff Engineer', category: 'Engineering' },
  { value: 'principal_engineer', label: 'Principal Engineer', category: 'Engineering' },
  { value: 'frontend_developer', label: 'Frontend Developer', category: 'Engineering' },
  { value: 'backend_developer', label: 'Backend Developer', category: 'Engineering' },
  { value: 'fullstack_developer', label: 'Full-Stack Developer', category: 'Engineering' },
  { value: 'mobile_developer', label: 'Mobile Developer', category: 'Engineering' },
  { value: 'devops_engineer', label: 'DevOps Engineer', category: 'Engineering' },
  { value: 'sre', label: 'SRE (Site Reliability Engineer)', category: 'Engineering' },
  { value: 'qa_engineer', label: 'QA Engineer', category: 'Engineering' },
  { value: 'tech_lead', label: 'Tech Lead', category: 'Engineering' },
  { value: 'engineering_manager', label: 'Engineering Manager', category: 'Engineering' },
  { value: 'architect', label: 'Software Architect', category: 'Engineering' },
  { value: 'security_engineer', label: 'Security Engineer', category: 'Engineering' },
  
  // Data & AI
  { value: 'data_scientist', label: 'Data Scientist', category: 'Data' },
  { value: 'data_engineer', label: 'Data Engineer', category: 'Data' },
  { value: 'data_analyst', label: 'Data Analyst', category: 'Data' },
  { value: 'ml_engineer', label: 'ML Engineer', category: 'Data' },
  { value: 'ai_researcher', label: 'AI Researcher', category: 'Data' },
  { value: 'bi_analyst', label: 'BI Analyst', category: 'Data' },
  
  // Product & Design
  { value: 'product_manager', label: 'Product Manager', category: 'Product' },
  { value: 'senior_product_manager', label: 'Senior Product Manager', category: 'Product' },
  { value: 'product_owner', label: 'Product Owner', category: 'Product' },
  { value: 'ux_designer', label: 'UX Designer', category: 'Design' },
  { value: 'ui_designer', label: 'UI Designer', category: 'Design' },
  { value: 'product_designer', label: 'Product Designer', category: 'Design' },
  { value: 'ux_researcher', label: 'UX Researcher', category: 'Design' },
  { value: 'design_lead', label: 'Design Lead', category: 'Design' },
  
  // Marketing & Growth
  { value: 'marketing_manager', label: 'Marketing Manager', category: 'Marketing' },
  { value: 'growth_manager', label: 'Growth Manager', category: 'Marketing' },
  { value: 'content_manager', label: 'Content Manager', category: 'Marketing' },
  { value: 'social_media_manager', label: 'Social Media Manager', category: 'Marketing' },
  { value: 'seo_specialist', label: 'SEO Specialist', category: 'Marketing' },
  { value: 'performance_marketer', label: 'Performance Marketer', category: 'Marketing' },
  { value: 'brand_manager', label: 'Brand Manager', category: 'Marketing' },
  { value: 'community_manager', label: 'Community Manager', category: 'Marketing' },
  
  // Sales & Business Development
  { value: 'sales_representative', label: 'Sales Representative', category: 'Sales' },
  { value: 'account_executive', label: 'Account Executive', category: 'Sales' },
  { value: 'account_manager', label: 'Account Manager', category: 'Sales' },
  { value: 'sales_manager', label: 'Sales Manager', category: 'Sales' },
  { value: 'business_developer', label: 'Business Developer', category: 'Sales' },
  { value: 'partnerships_manager', label: 'Partnerships Manager', category: 'Sales' },
  { value: 'customer_success', label: 'Customer Success Manager', category: 'Sales' },
  { value: 'sales_engineer', label: 'Sales Engineer', category: 'Sales' },
  
  // Operations & Support
  { value: 'operations_manager', label: 'Operations Manager', category: 'Operations' },
  { value: 'project_manager', label: 'Project Manager', category: 'Operations' },
  { value: 'scrum_master', label: 'Scrum Master', category: 'Operations' },
  { value: 'support_engineer', label: 'Support Engineer', category: 'Operations' },
  { value: 'customer_support', label: 'Customer Support', category: 'Operations' },
  { value: 'office_manager', label: 'Office Manager', category: 'Operations' },
  
  // Finance & Legal
  { value: 'finance_manager', label: 'Finance Manager', category: 'Finance' },
  { value: 'financial_controller', label: 'Financial Controller', category: 'Finance' },
  { value: 'accountant', label: 'Accountant', category: 'Finance' },
  { value: 'legal_counsel', label: 'Legal Counsel', category: 'Legal' },
  
  // HR & People
  { value: 'hr_manager', label: 'HR Manager', category: 'People' },
  { value: 'people_ops', label: 'People Operations', category: 'People' },
  { value: 'talent_acquisition', label: 'Talent Acquisition', category: 'People' },
  { value: 'recruiter', label: 'Recruiter', category: 'People' },
] as const;

// ============================================================================
// FONCTIONS POUR LES ACTIONNAIRES/ASSOCIÉS
// ============================================================================

// Fonctions/Qualités pour les actionnaires (sociétés par actions)
export const SHAREHOLDER_FUNCTIONS = [
  { value: 'actionnaire_fondateur', label: 'Actionnaire Fondateur', description: 'Fondateur de l\'entreprise' },
  { value: 'actionnaire_majoritaire', label: 'Actionnaire Majoritaire', description: 'Détient plus de 50% du capital' },
  { value: 'actionnaire_minoritaire', label: 'Actionnaire Minoritaire', description: 'Détient moins de 50% du capital' },
  { value: 'actionnaire_reference', label: 'Actionnaire de Référence', description: 'Actionnaire stratégique significatif' },
  { value: 'investisseur_institutionnel', label: 'Investisseur Institutionnel', description: 'Fonds, banque, assurance' },
  { value: 'business_angel', label: 'Business Angel', description: 'Investisseur individuel' },
  { value: 'venture_capital', label: 'Venture Capital (VC)', description: 'Fonds de capital-risque' },
  { value: 'private_equity', label: 'Private Equity', description: 'Fonds d\'investissement' },
  { value: 'administrateur', label: 'Administrateur', description: 'Membre du conseil d\'administration' },
  { value: 'censeur', label: 'Censeur', description: 'Membre consultatif du conseil' },
] as const;

// Fonctions/Qualités pour les associés (SARL, SNC, etc.)
export const PARTNER_FUNCTIONS = [
  { value: 'associe_gerant', label: 'Associé Gérant', description: 'Associé avec pouvoirs de gestion' },
  { value: 'associe_fondateur', label: 'Associé Fondateur', description: 'Fondateur de la société' },
  { value: 'associe_majoritaire', label: 'Associé Majoritaire', description: 'Détient plus de 50% des parts' },
  { value: 'associe_minoritaire', label: 'Associé Minoritaire', description: 'Détient moins de 50% des parts' },
  { value: 'associe_commandite', label: 'Associé Commandité', description: 'Responsabilité illimitée (SCS)' },
  { value: 'associe_commanditaire', label: 'Associé Commanditaire', description: 'Responsabilité limitée (SCS)' },
  { value: 'associe_unique', label: 'Associé Unique', description: 'SARLU - Seul associé' },
] as const;

// Fonctions pour les membres de GIE
export const GIE_MEMBER_FUNCTIONS = [
  { value: 'membre_fondateur', label: 'Membre Fondateur', description: 'Fondateur du GIE' },
  { value: 'membre_adherent', label: 'Membre Adhérent', description: 'Membre ayant adhéré' },
  { value: 'administrateur_gie', label: 'Administrateur', description: 'Membre de l\'administration' },
  { value: 'president_gie', label: 'Président', description: 'Président du GIE' },
] as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export type CompanyTypeForFunctions = 'traditional' | 'startup';
export type PersonTypeForFunctions = 'dirigeant' | 'employe' | 'actionnaire';

export interface FunctionOption {
  value: string;
  label: string;
  description?: string;
  category?: string;
}

/**
 * Retourne les fonctions disponibles selon le type d'entreprise et le type de personne
 */
export const getFunctionsForPersonType = (
  companyType: CompanyTypeForFunctions,
  personType: PersonTypeForFunctions,
  shareType?: string // Pour les actionnaires
): FunctionOption[] => {
  switch (personType) {
    case 'dirigeant':
      const commonExec = COMMON_EXECUTIVE_FUNCTIONS.map(f => ({ ...f }));
      const specificExec = companyType === 'startup' 
        ? STARTUP_EXECUTIVE_FUNCTIONS.map(f => ({ ...f }))
        : PME_EXECUTIVE_FUNCTIONS.map(f => ({ ...f }));
      return [...commonExec, ...specificExec];
    
    case 'employe':
      return companyType === 'startup'
        ? STARTUP_EMPLOYEE_FUNCTIONS.map(f => ({ ...f }))
        : PME_EMPLOYEE_FUNCTIONS.map(f => ({ ...f }));
    
    case 'actionnaire':
      switch (shareType) {
        case 'actions':
          return SHAREHOLDER_FUNCTIONS.map(f => ({ ...f }));
        case 'parts_sociales':
          return PARTNER_FUNCTIONS.map(f => ({ ...f }));
        case 'parts_gie':
          return GIE_MEMBER_FUNCTIONS.map(f => ({ ...f }));
        default:
          return [...SHAREHOLDER_FUNCTIONS, ...PARTNER_FUNCTIONS].map(f => ({ ...f }));
      }
    
    default:
      return [];
  }
};

/**
 * Groupe les fonctions par catégorie
 */
export const groupFunctionsByCategory = (functions: FunctionOption[]): Record<string, FunctionOption[]> => {
  return functions.reduce((acc, func) => {
    const category = func.category || 'Général';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(func);
    return acc;
  }, {} as Record<string, FunctionOption[]>);
};

/**
 * Recherche dans les fonctions
 */
export const searchFunctions = (
  functions: FunctionOption[],
  query: string
): FunctionOption[] => {
  if (!query.trim()) return functions;
  
  const normalizedQuery = query.toLowerCase().trim();
  
  return functions.filter(func => 
    func.label.toLowerCase().includes(normalizedQuery) ||
    func.value.toLowerCase().includes(normalizedQuery) ||
    (func.description && func.description.toLowerCase().includes(normalizedQuery))
  );
};

/**
 * Trouve une fonction par sa valeur
 */
export const findFunctionByValue = (
  functions: FunctionOption[],
  value: string
): FunctionOption | undefined => {
  return functions.find(f => f.value === value);
};

// Constante "Autre" pour permettre la saisie libre
export const OTHER_FUNCTION: FunctionOption = {
  value: 'autre',
  label: 'Autre (préciser)',
  description: 'Saisissez une fonction personnalisée'
};
